import { Link } from 'react-router-dom'
import { useDevices } from '../api/hooks'
import type { Device } from '../api/client'

const STATUS_STYLES: Record<Device['status'], string> = {
  online:   'bg-green-100 text-green-800',
  offline:  'bg-red-100 text-red-800',
  degraded: 'bg-yellow-100 text-yellow-800',
  unknown:  'bg-gray-100 text-gray-600',
}

function timeSince(iso: string): string {
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (secs < 60)  return `${secs}s ago`
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`
  return `${Math.floor(secs / 86400)}d ago`
}

export default function DevicesPage() {
  const { data, isLoading, isError, error } = useDevices()

  return (
    <div className="px-8 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Devices</h1>
          <p className="text-sm text-gray-400 mt-0.5">Auto-registered from telemetry</p>
        </div>
        {data && (
          <span className="text-sm text-gray-500">
            {data.count} device{data.count !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {isLoading && (
        <div className="text-sm text-gray-400 py-12 text-center">Loading devices…</div>
      )}

      {isError && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          {error instanceof Error ? error.message : 'Failed to load devices'}
        </div>
      )}

      {data && data.devices.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <div className="text-4xl mb-3">📡</div>
          <p className="text-sm font-medium">No devices yet</p>
          <p className="text-xs mt-1">Devices appear automatically when they send their first telemetry.</p>
        </div>
      )}

      {data && data.devices.length > 0 && (
        <div className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">Device ID</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">Status</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">Last seen</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">Type</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">First seen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data.devices.map((d: Device) => (
                <tr key={d.id} className="hover:bg-gray-50/50 transition-colors cursor-pointer">
                  <td className="px-4 py-3 font-mono text-xs">
                    <Link to={`/devices/${d.id}`} className="text-brand-600 hover:underline">
                      {d.external_id}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[d.status as Device['status']] ?? 'bg-gray-100 text-gray-600'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${d.status === 'online' ? 'bg-green-500' : d.status === 'offline' ? 'bg-red-500' : d.status === 'degraded' ? 'bg-yellow-500' : 'bg-gray-400'}`} />
                      {d.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{timeSince(d.last_seen)}</td>
                  <td className="px-4 py-3 text-gray-500">{d.device_type || '—'}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{new Date(d.first_seen).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
